import enProjectEstimator from '../../../../public/locales/en/pageProjectEstimator.json';
import hrProjectEstimator from '../../../../public/locales/hr/pageProjectEstimator.json';
import {
  ADMIN_EXPERIENCE_PREFERENCE_OPTIONS,
  API_ENDPOINT_COUNT_OPTIONS,
  AUTHENTICATION_OPTIONS,
  BUDGET_OPTIONS,
  CAPABILITY_OPTIONS,
  CATALOG_COMPLEXITY_OPTIONS,
  CATALOG_SIZE_OPTIONS,
  CHECKOUT_COMPLEXITY_OPTIONS,
  COMMERCE_LOGIC_COMPLEXITY_OPTIONS,
  COMPLIANCE_OPTIONS,
  IMPLEMENTATION_PATH_PREFERENCE_OPTIONS,
  INTEGRATION_OPTIONS,
  INVENTORY_COMPLEXITY_OPTIONS,
  MIGRATION_OPTIONS,
  PRODUCT_MODEL_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  QUEUE_WORKFLOW_COUNT_OPTIONS,
  SURFACE_OPTIONS,
  TIMELINE_OPTIONS,
  USER_COUNT_OPTIONS,
  USER_ROLE_OPTIONS,
  WEBHOOK_FLOW_COUNT_OPTIONS,
} from './constants';
import { IEstimatorOption } from './types';

type OptionOverride = {
  description?: string;
  label?: string;
  tooltip?: string;
};

type CostDriver = {
  description: string;
  key: string;
  title: string;
};

type EstimatorTranslations = {
  actions: {
    back: string;
    continue: string;
    generateEstimate: string;
  };
  costDrivers: CostDriver[];
  disclaimer: string;
  hero: {
    bullets: string[];
    description: string;
    eyebrow: string;
    title: string;
  };
  labels: {
    adminExperiencePreference: string;
    about: string;
    apiEndpointCount: string;
    auth: string;
    backendCompliance: string;
    budget: string;
    catalogComplexity: string;
    catalogSize: string;
    checkoutComplexity: string;
    commerceLogicComplexity: string;
    company: string;
    compliance: string;
    description: string;
    descriptionHelp: string;
    email: string;
    implementationPathPreference: string;
    integrations: string;
    inventoryComplexity: string;
    keyCapabilities: string;
    migration: string;
    name: string;
    phone: string;
    productModel: string;
    projectType: string;
    queueWorkflowCount: string;
    surfacesBackendOnlyNote: string;
    surfaces: string;
    timeline: string;
    userCount: string;
    userCountWebshop: string;
    userRoles: string;
    webhookFlowCount: string;
    whereFrom: string;
  };
  options: {
    adminExperiencePreference: OptionOverride[];
    apiEndpointCount: OptionOverride[];
    authentication: OptionOverride[];
    backendCompliance: OptionOverride[];
    budget: OptionOverride[];
    catalogComplexity: OptionOverride[];
    catalogSize: OptionOverride[];
    capabilities: OptionOverride[];
    checkoutComplexity: OptionOverride[];
    commerceLogicComplexity: OptionOverride[];
    compliance: OptionOverride[];
    implementationPathPreference: OptionOverride[];
    integrations: OptionOverride[];
    inventoryComplexity: OptionOverride[];
    migration: OptionOverride[];
    productModel: OptionOverride[];
    projectType: OptionOverride[];
    queueWorkflowCount: OptionOverride[];
    surfaces: OptionOverride[];
    timeline: OptionOverride[];
    userCount: OptionOverride[];
    userRoles: OptionOverride[];
    webhookFlowCount: OptionOverride[];
  };
  progress: string;
  report: {
    calculationDetails: string;
    complexity: string;
    costHeading: string;
    costSnapshot: string;
    deliveryStatusFailed: string;
    deliveryStatusPending: string;
    deliveryStatusSent: string;
    estimateExplanation: string;
    estimateExplanationTemplate: string;
    estimateExplanationBullets: string[];
    estimatedHoursSuffix: string;
    finalStepIntro: string;
    finalThankYouDescription: string;
    finalThankYouEyebrow: string;
    finalThankYouTitle: string;
    fitHeading: string;
    indicativeOnly: string;
    investmentRange: string;
    nextSteps: string;
    noMajorRisksFallback: string;
    recommendedStack: string;
    reportHeading: string;
    reportIntro: string;
    requirements: string;
    risks: string;
    team: string;
    timeline: string;
    timelineNote: string;
    webshopPathAlternatives: string;
    webshopPathHeading: string;
    webshopPathRecommended: string;
  };
  scoring: {
    allocations: {
      full: string;
      part: string;
    };
    breakdownLabels: {
      impliedFromDescription: string;
      userRolesCoordination: string;
      userScalePlanning: string;
    };
    complexity: {
      focused: string;
      high: string;
      medium: string;
      mediumHigh: string;
    };
    fitNotes: {
      budgetCompatible: string;
      budgetSupportsDiscovery: string;
      noBudget: string;
      overBudgetTemplate: string;
    };
    leadScore: {
      goodFit: {
        label: string;
        note: string;
      };
      highPotential: {
        label: string;
        note: string;
      };
      narrowPhase: {
        label: string;
        note: string;
      };
    };
    nextSteps: string[];
    recommendedStack: {
      adminPanel: string;
      backendApi: string;
      enterpriseAuth: string;
      mobileApp: string;
      nextWebApp: string;
      offlineStrategy: string;
      postgres: string;
    };
    suggestedTeam: {
      ai: string;
      backend: string;
      discovery: string;
      frontend: string;
      mobile: string;
      pm: string;
      qa: string;
    };
    summaryFallback: string;
    timelineTemplate: string;
  };
  validation: {
    adminExperiencePreferenceRequired: string;
    apiEndpointCountRequired: string;
    approximateBudgetRangeRequired: string;
    authenticationRequirementsMin: string;
    catalogComplexityRequired: string;
    catalogSizeRequired: string;
    checkoutComplexityRequired: string;
    contactNameRequired: string;
    commerceLogicComplexityRequired: string;
    emailInvalid: string;
    emailRequired: string;
    estimatedUserCountRequired: string;
    existingSystemMigrationRequired: string;
    expectedTimelineRequired: string;
    implementationPathPreferenceRequired: string;
    inventoryComplexityRequired: string;
    keyCapabilitiesMin: string;
    productModelRequired: string;
    projectDescriptionMeaningful: string;
    projectDescriptionMin: string;
    projectDescriptionRequired: string;
    projectTypeRequired: string;
    preferredSurfacesMin: string;
    queueWorkflowCountRequired: string;
    submitError: string;
    userRolesRequired: string;
    webhookFlowCountRequired: string;
  };
  steps: {
    commercialFit: {
      description: string;
      title: string;
    };
    contactReport: {
      description: string;
      title: string;
    };
    operationsRisk: {
      description: string;
      title: string;
    };
    productScope: {
      description: string;
      title: string;
    };
    projectDirection: {
      description: string;
      title: string;
    };
    webshopCommerce: {
      description: string;
      title: string;
    };
  };
};

export type IEstimatorCopy = Omit<EstimatorTranslations, 'options'> & {
  options: {
    adminExperiencePreference: IEstimatorOption[];
    apiEndpointCount: IEstimatorOption[];
    authentication: IEstimatorOption[];
    backendCompliance: IEstimatorOption[];
    budget: IEstimatorOption[];
    catalogComplexity: IEstimatorOption[];
    catalogSize: IEstimatorOption[];
    capabilities: IEstimatorOption[];
    checkoutComplexity: IEstimatorOption[];
    commerceLogicComplexity: IEstimatorOption[];
    compliance: IEstimatorOption[];
    implementationPathPreference: IEstimatorOption[];
    integrations: IEstimatorOption[];
    inventoryComplexity: IEstimatorOption[];
    migration: IEstimatorOption[];
    productModel: IEstimatorOption[];
    projectType: IEstimatorOption[];
    queueWorkflowCount: IEstimatorOption[];
    surfaces: IEstimatorOption[];
    timeline: IEstimatorOption[];
    userCount: IEstimatorOption[];
    userRoles: IEstimatorOption[];
    webhookFlowCount: IEstimatorOption[];
  };
};

const withOptionOverrides = (options: OptionOverride[], base: IEstimatorOption[]) =>
  base.map((item, index) => ({
    ...item,
    description: options[index]?.description ?? item.description,
    label: options[index]?.label || item.label,
    tooltip: options[index]?.tooltip ?? item.tooltip,
  }));

const estimatorCopyByLanguage: Record<'en' | 'hr', EstimatorTranslations> = {
  en: enProjectEstimator.estimator as EstimatorTranslations,
  hr: hrProjectEstimator.estimator as EstimatorTranslations,
};

export const getEstimatorCopy = (language: 'en' | 'hr'): IEstimatorCopy => {
  const translations = estimatorCopyByLanguage[language];

  return {
    ...translations,
    options: {
      adminExperiencePreference: withOptionOverrides(
        translations.options.adminExperiencePreference,
        ADMIN_EXPERIENCE_PREFERENCE_OPTIONS,
      ),
      apiEndpointCount: withOptionOverrides(
        translations.options.apiEndpointCount,
        API_ENDPOINT_COUNT_OPTIONS,
      ),
      authentication: withOptionOverrides(
        translations.options.authentication,
        AUTHENTICATION_OPTIONS,
      ),
      backendCompliance: withOptionOverrides(
        translations.options.backendCompliance,
        COMPLIANCE_OPTIONS,
      ),
      budget: withOptionOverrides(translations.options.budget, BUDGET_OPTIONS),
      catalogComplexity: withOptionOverrides(
        translations.options.catalogComplexity,
        CATALOG_COMPLEXITY_OPTIONS,
      ),
      catalogSize: withOptionOverrides(translations.options.catalogSize, CATALOG_SIZE_OPTIONS),
      capabilities: withOptionOverrides(translations.options.capabilities, CAPABILITY_OPTIONS),
      checkoutComplexity: withOptionOverrides(
        translations.options.checkoutComplexity,
        CHECKOUT_COMPLEXITY_OPTIONS,
      ),
      commerceLogicComplexity: withOptionOverrides(
        translations.options.commerceLogicComplexity,
        COMMERCE_LOGIC_COMPLEXITY_OPTIONS,
      ),
      compliance: withOptionOverrides(translations.options.compliance, COMPLIANCE_OPTIONS),
      implementationPathPreference: withOptionOverrides(
        translations.options.implementationPathPreference,
        IMPLEMENTATION_PATH_PREFERENCE_OPTIONS,
      ),
      integrations: withOptionOverrides(translations.options.integrations, INTEGRATION_OPTIONS),
      inventoryComplexity: withOptionOverrides(
        translations.options.inventoryComplexity,
        INVENTORY_COMPLEXITY_OPTIONS,
      ),
      migration: withOptionOverrides(translations.options.migration, MIGRATION_OPTIONS),
      productModel: withOptionOverrides(translations.options.productModel, PRODUCT_MODEL_OPTIONS),
      projectType: withOptionOverrides(translations.options.projectType, PROJECT_TYPE_OPTIONS),
      queueWorkflowCount: withOptionOverrides(
        translations.options.queueWorkflowCount,
        QUEUE_WORKFLOW_COUNT_OPTIONS,
      ),
      surfaces: withOptionOverrides(translations.options.surfaces, SURFACE_OPTIONS),
      timeline: withOptionOverrides(translations.options.timeline, TIMELINE_OPTIONS),
      userCount: withOptionOverrides(translations.options.userCount, USER_COUNT_OPTIONS),
      userRoles: withOptionOverrides(translations.options.userRoles, USER_ROLE_OPTIONS),
      webhookFlowCount: withOptionOverrides(
        translations.options.webhookFlowCount,
        WEBHOOK_FLOW_COUNT_OPTIONS,
      ),
    },
  };
};
